// src/App.js
import React, { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { lightTheme, darkTheme } from "./styles/theme";

// Auth
import Login from "./auth/Login";
import Signup from "./auth/Signup";
import VerifyEmail from "./auth/VerifyEmail";
import ForgotPassword from "./auth/ForgotPassword";
import VerifyResetCode from "./auth/VerifyResetCode";
import ResetPassword from "./auth/ResetPassword";

// Dashboards
import UserDashboard from "./user/UserDashboard";
import AdminDashboard from "./admin/AdminDashboard";
import DirectorDashboard from "./director/DirectorDashboard";

// Dashboard pages
import EarningsSummary from "./user/EarningsSummary";
import InviteFriends from "./user/InviteFriends";
import ReferralTree from "./user/ReferralTree";
import TokenLedger from "./user/TokenLedger";
import UpdateProfile from "./user/UpdateProfile";
import WithdrawalHistory from "./user/WithdrawalHistory";
import WithdrawalRequest from "./user/WithdrawalRequest";
import VideoUpload from "./user/VideoUpload";
import UserVideoList from "./user/UserVideoList";
import StripeKYCVerification from "./user/StripeKYCVerification";
import WithdrawHub from './user/WithdrawHub';

// Channels
import ChannelsList from "./user/ChannelsList";
import ChannelDetail from "./user/ChannelDetail";
import ChannelManage from "./user/ChannelManage";
import ChannelEdit from "./user/ChannelEdit";
import VideoEdit from "./user/VideoEdit";

// Component to handle protected route redirects intelligently
function ProtectedRouteRedirect({ isAdmin, isDirector }) {
  const location = useLocation();
  
  // If we're already on an admin route, preserve it (AdminDashboard has catch-all that handles sub-routes)
  // This ensures that when page is refreshed on an admin route, it stays on that route
  if (location.pathname.startsWith('/admin')) {
    // Don't redirect - let AdminDashboard handle the routing
    // The AdminDashboard component has a catch-all route that will show the appropriate page
    // Return a fragment to satisfy React Router
    return <></>;
  }
  
  // If we're already on a director route, preserve it (DirectorDashboard has catch-all that handles sub-routes)
  if (location.pathname.startsWith('/director')) {
    // Don't redirect - let DirectorDashboard handle the routing
    return <></>;
  }
  
  // If we're already on a dashboard route, preserve it (UserDashboard handles sub-routing)
  // This ensures that when page is refreshed on a user dashboard route, it stays on that route
  if (location.pathname.startsWith('/dashboard')) {
    // Don't redirect - let UserDashboard handle the routing
    return <></>;
  }
  
  // Default redirect: Always go to user dashboard for unmatched routes
  // This ensures all users (including admins and directors) land on user dashboard after login
  return <Navigate to="/dashboard" replace />;
}

function App() {
  // Check token immediately to determine admin status for routing
  const token = localStorage.getItem("token");
  const getInitialAdminStatus = () => {
    if (!token) return false;
    try {
      const decoded = jwtDecode(token);
      return decoded.userTypeId === 1;
    } catch {
      return false;
    }
  };
  
  const getInitialContentCreatorStatus = () => {
    if (!token) return false;
    try {
      const decoded = jwtDecode(token);
      return !!decoded.isContentCreator;
    } catch {
      return false;
    }
  };

  const getInitialDirectorStatus = () => {
    if (!token) return false;
    try {
      const decoded = jwtDecode(token);
      return decoded.userTypeId === 4;
    } catch {
      return false;
    }
  };

  const [loggedIn, setLoggedIn] = useState(!!token);
  const [isAdmin, setIsAdmin] = useState(getInitialAdminStatus());
  const [isContentCreator, setIsContentCreator] = useState(getInitialContentCreatorStatus());
  const [isDirector, setIsDirector] = useState(getInitialDirectorStatus());
  // Always use dark mode
  const [darkMode, setDarkMode] = useState(true);

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setIsAdmin(decoded.userTypeId === 1);
        setIsContentCreator(!!decoded.isContentCreator);
        setIsDirector(decoded.userTypeId === 4);
      } catch {
        setLoggedIn(false);
        setIsAdmin(false);
        setIsContentCreator(false);
        setIsDirector(false);
        localStorage.removeItem("token");
      }
    }
  }, [token]);

  const handleLogin = () => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setIsAdmin(decoded.userTypeId === 1);
        setIsContentCreator(!!decoded.isContentCreator);
        setIsDirector(decoded.userTypeId === 4);
      } catch {
        setIsAdmin(false);
        setIsContentCreator(false);
        setIsDirector(false);
      }
    }
    setLoggedIn(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    setLoggedIn(false);
    setIsAdmin(false);
    setIsContentCreator(false);
    setIsDirector(false);
  };

  // Dark mode toggle
  const toggleDarkMode = () => {
    setDarkMode(prev => !prev);
  };

  const currentTheme = darkMode ? darkTheme : lightTheme;

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      <Router>
        <Routes>
        {!loggedIn ? (
          <>
            <Route path="/login" element={<Login onLogin={handleLogin} />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/verify-reset-code" element={<VerifyResetCode />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="*" element={<Navigate to="/login" />} />
          </>
        ) : (
          <>
            {/* ✅ Dashboard wrapper with nested children */}
            <Route
              path="/dashboard/*"
              element={
                <UserDashboard
                  isAdmin={isAdmin}
                  isDirector={isDirector}
                  isContentCreator={isContentCreator}
                  onLogout={handleLogout}
                  darkMode={darkMode}
                  onToggleDarkMode={toggleDarkMode}
                />
              }
            >
              <Route index element={<Navigate to="engagement" replace />} />
              <Route path="engagement" element={<EarningsSummary />} />
              <Route path="invite" element={<InviteFriends />} />
              <Route path="community" element={<ReferralTree />} />
              <Route path="activity" element={<TokenLedger />} />
              <Route path="profile" element={<UpdateProfile />} />
              <Route path="kyc-verification" element={<StripeKYCVerification />} />
              <Route path="withdrawals" element={<WithdrawalHistory />} />
              <Route path="request" element={<WithdrawalRequest />} />
              <Route path="redemptions" element={<WithdrawHub />} />
              {isContentCreator && (
                <>
                  <Route path="video-upload" element={<VideoUpload />} />
                  <Route path="my-videos" element={<UserVideoList />} />
                </>
              )}
            </Route>

            {/* Admin dashboard */}
            {isAdmin && (
              <Route
                path="/admin/*"
                element={
                  <AdminDashboard 
                    onLogout={handleLogout}
                    darkMode={darkMode}
                    onToggleDarkMode={toggleDarkMode}
                  />
                }
              />
            )}

            {/* Directors dashboard */}
            {isDirector && (
              <Route
                path="/director/*"
                element={
                  <DirectorDashboard 
                    onLogout={handleLogout}
                    darkMode={darkMode}
                    onToggleDarkMode={toggleDarkMode}
                  />
                }
              />
            )}

            <Route path="*" element={<ProtectedRouteRedirect isAdmin={isAdmin} isDirector={isDirector} />} />
          </>
        )}
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
